import { useApolloClient } from '@apollo/client';
import { message } from 'antd';
import { Formik } from 'formik';
import { pick } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
// eslint-disable-next-line max-len
import { type SimpleVehicleManagementModuleSpecsFragment } from '../../../../api/fragments/SimpleVehicleManagementModuleSpecs';
import {
    UpdateSimpleVehicleManagementModuleDocument,
    type UpdateSimpleVehicleManagementModuleMutation,
    type UpdateSimpleVehicleManagementModuleMutationVariables,
} from '../../../../api/mutations/updateSimpleVehicleManagementModule';
import { type SimpleVehicleManagementUpdateSettings, TemplateType } from '../../../../api/types';
import Form from '../../../../components/fields/Form';
import useHandleError from '../../../../utilities/useHandleError';
import useValidator from '../../../../utilities/useValidator';
import validators from '../../../../utilities/validators';
import SimpleVehicleManagementForm from './SimpleVehicleManagementForm';

type SimpleManagementModuleInnerPageProps = {
    module: SimpleVehicleManagementModuleSpecsFragment;
};

export type SimpleManagementModuleInnerPageFormValues = SimpleVehicleManagementUpdateSettings;

const validator = validators.compose(validators.requiredStringEnum('defaultTemplate', TemplateType));

const SimpleVehicleManagementModuleInnerPage = ({ module }: SimpleManagementModuleInnerPageProps) => {
    const { t } = useTranslation('simpleVehicleManagementDetail');
    const apolloClient = useApolloClient();
    const validate = useValidator(validator);

    const initialValues: SimpleManagementModuleInnerPageFormValues = useMemo(
        () => ({
            ...pick(['defaultTemplate', 'porscheMasterDataModuleId'], module),
        }),
        [module]
    );

    const onSubmit = useHandleError(
        async (values: SimpleManagementModuleInnerPageFormValues) => {
            message.loading({
                content: t('simpleVehicleManagementDetail:messages.submitting'),
                key: 'primary',
                duration: 0,
            });
            const settings: SimpleVehicleManagementUpdateSettings = {
                ...pick(['defaultTemplate', 'porscheMasterDataModuleId'], values),
            };
            await apolloClient
                .mutate<
                    UpdateSimpleVehicleManagementModuleMutation,
                    UpdateSimpleVehicleManagementModuleMutationVariables
                >({
                    mutation: UpdateSimpleVehicleManagementModuleDocument,
                    variables: { moduleId: module.id, settings },
                })
                .finally(() => {
                    message.destroy('primary');
                });
            message.success({
                content: t('simpleVehicleManagementDetail:messages.success'),
                key: 'primary',
            });
        },
        [apolloClient, t, module.id]
    );

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <Form id="updateForm" name="updateForm" onSubmitCapture={handleSubmit}>
                    <SimpleVehicleManagementForm module={module} />
                </Form>
            )}
        </Formik>
    );
};

export default SimpleVehicleManagementModuleInnerPage;
